import { useCallback, useEffect, useState } from 'react';
import { getText } from 'i18n/intl';
import { isEmpty } from 'lodash';
import isEqual from 'lodash/isEqual';
import { calendarClient } from 'services/calendarClient';
import { fetchFields } from 'slices/field.slice';
import { requestObjectTypes } from 'slices/objectTypes.slice';
import { fetchReservationModes } from 'slices/reservationMode.slice';
import { useAppDispatch } from 'slices/store';
import { getErrorMessage } from 'utils/errorUtils';
import { message } from 'utils/messageUtils';
import { errorNotification } from 'utils/notifications';
import type { ObjectMapping } from '@timeedit/calendar-integration-api-contract';
import { Button, DefaultContentLayout } from '@timeedit/ui-components';
import { Form, Space } from '@timeedit/ui-components/antd';
import { useForm } from '@timeedit/ui-components/antd/form/Form';
import { ObjectMappingSection } from './Sections/ObjectMappingSection';

export function MS365MappingPage() {
  const dispatch = useAppDispatch();

  const [form] = useForm<ObjectMapping>();

  const [storedObjectMapping, setStoredObjectMapping] =
    useState<ObjectMapping | null>(null);
  const [hasMappingChanges, setHasMappingChanges] = useState(false);

  useEffect(() => {
    dispatch(fetchReservationModes());
    dispatch(fetchFields());
    dispatch(requestObjectTypes());
  }, []);

  useEffect(() => {
    calendarClient.objectMapping
      .getAll()
      .then((response) => {
        if (response.status !== 200) {
          return errorNotification(getErrorMessage(response));
        }

        const objectMappings = response.body.results;

        if (objectMappings.length === 0) {
          setStoredObjectMapping(null);
          form.setFieldsValue({});
          return;
        }

        setStoredObjectMapping(objectMappings[0]);
        form.setFieldsValue(objectMappings[0]);
      })
      .catch((error) => {
        errorNotification(getErrorMessage(error));
      });
  }, []);

  const createOrUpdateObjectMapping = useCallback(
    (objectMapping: ObjectMapping) => {
      const body: ObjectMapping = { id: objectMapping.id };

      Object.entries(objectMapping).forEach(([key, value]) => {
        if (!isEmpty(value) && Object.values(value).some(Boolean)) {
          body[key] = value;
        } else {
          body[key] = null;
        }
      });

      if (storedObjectMapping === null) {
        return calendarClient.objectMapping.create({
          body,
        });
      }

      return calendarClient.objectMapping.update({
        params: { id: storedObjectMapping.id },
        body,
      });
    },
    [storedObjectMapping]
  );

  const onMappingChange = useCallback(
    (changes: Partial<ObjectMapping>) => {
      if (
        Object.hasOwn(changes, 'room') &&
        Object.hasOwn(changes.room, 'objectTypeId')
      ) {
        form.setFieldsValue({
          room: {
            nameFieldId: undefined,
            objectFieldId: undefined,
          },
        });
      }

      setHasMappingChanges(
        !isEqual(storedObjectMapping, form.getFieldsValue())
      );
    },
    [storedObjectMapping]
  );

  const onDiscardChanges = () => {
    form.resetFields();
    setHasMappingChanges(false);
  };

  const onSaveChanges = useCallback(async () => {
    try {
      await form.validateFields();
    } catch {
      // Ignore validation errors as it is displayed in the form already
      return;
    }

    try {
      const objectMapping = form.getFieldsValue();

      const response = await createOrUpdateObjectMapping(objectMapping);

      if (response.status >= 200 && response.status < 300) {
        setStoredObjectMapping(objectMapping);
        setHasMappingChanges(false);
        message.success(
          getText('integration.calendar.mapping.success.save_successful')
        );
        return;
      }

      errorNotification(getErrorMessage(response));
    } catch (error) {
      errorNotification(
        getErrorMessage(error) ||
          getText('integration.calendar.mapping.error.save_failed')
      );
    }
  }, [createOrUpdateObjectMapping]);

  return (
    <DefaultContentLayout
      title={getText('integration.calendar.mapping.title')}
      subtitle={getText('integration.calendar.mapping.subtitle')}
      actions={[
        <Button
          key="discard"
          disabled={!hasMappingChanges}
          onClick={onDiscardChanges}
        >
          {getText('integration.calendar.mapping.actions.discard_changes')}
        </Button>,
        <Button
          key="save"
          type="primary"
          disabled={!hasMappingChanges}
          onClick={onSaveChanges}
        >
          {getText('integration.calendar.mapping.actions.save_changes')}
        </Button>,
      ]}
    >
      <div className="ms365-mapping">
        <div className="content">
          <Form
            form={form}
            initialValues={storedObjectMapping ?? {}}
            labelAlign="left"
            labelCol={{ style: { width: '35%' } }}
            onValuesChange={onMappingChange}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item hidden name="id" />
              <ObjectMappingSection
                title={getText('integration.calendar.mapping.room.title')}
                name="room"
              />
              <ObjectMappingSection
                title={getText('integration.calendar.mapping.staff.title')}
                name="staff"
              />
              <ObjectMappingSection
                title={getText('integration.calendar.mapping.student.title')}
                name="student"
              />
            </Space>
          </Form>
        </div>
      </div>
    </DefaultContentLayout>
  );
}
